import { useState, useEffect, useCallback } from 'react';
import { getAdminSupportTickets } from '../../api/supportTicketApi';
import { fmtIST } from '../../utils/dateFormat';
import AdminViewSupportTicket from './AdminViewSupportTicket';

const cyan = '#00e5ff';

const statusColors = {
  open: '#ffaa44',
  in_progress: '#66aaff',
  resolved: '#66ff99',
  closed: '#888',
};

const AdminSupportTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getAdminSupportTickets({
      status: status !== 'all' ? status : undefined,
      search: search || undefined,
      page,
      limit: 20,
    })
      .then((res) => {
        setTickets(res.data.tickets);
        setTotal(res.data.total);
        setPages(res.data.pages);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [status, search, page]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  return (
    <div>
      <h4 style={{ color: '#fff', fontWeight: 700, marginBottom: 4 }}>Support Tickets</h4>
      <div style={{ color: '#555', fontSize: 13, marginBottom: 24 }}>{total} total tickets</div>

      {/* Filters & Search */}
      <div style={{ marginBottom: 20 }}>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by ticket ID, title, user code..."
            style={{ flex: 1, minWidth: 200, background: 'rgba(0,229,255,0.04)', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 8, padding: '9px 14px', color: '#fff', fontSize: 13, outline: 'none' }}
          />
          <button type="submit" style={{ background: cyan, border: 'none', color: '#000', borderRadius: 8, padding: '9px 20px', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Search
          </button>
          {search && (
            <button type="button" onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }} style={{ background: 'rgba(255,255,255,0.06)', border: 'none', color: '#888', borderRadius: 8, padding: '9px 14px', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              Clear
            </button>
          )}
        </form>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
          {/* Status Filter */}
          <div>
            <label style={{ display: 'block', color: '#888', fontSize: 11, fontWeight: 600, marginBottom: 6 }}>Status</label>
            <select
              value={status}
              onChange={(e) => { setStatus(e.target.value); setPage(1); }}
              style={{
                width: '100%',
                background: '#1a1a1a',
                border: '1px solid rgba(0,229,255,0.2)',
                borderRadius: 8,
                padding: '8px 12px',
                color: '#fff',
                fontSize: 13,
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              <option value="all">All</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          {/* Clear Filters */}
          {status !== 'all' && (
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button
                onClick={() => { setStatus('all'); setPage(1); }}
                style={{
                  width: '100%',
                  background: 'rgba(255,255,255,0.06)',
                  border: 'none',
                  color: '#888',
                  borderRadius: 8,
                  padding: '8px 12px',
                  fontSize: 13,
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'rgba(0,229,255,0.07)', borderBottom: '1px solid rgba(0,229,255,0.12)' }}>
              {['Ticket ID', 'User', 'Title', 'Status', 'Created', ''].map((h) => (
                <th key={h} style={{ color: cyan, fontSize: 12, fontWeight: 600, padding: '12px 14px', textAlign: 'left', whiteSpace: 'nowrap' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>
                  Loading...
                </td>
              </tr>
            ) : tickets.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>
                  No tickets found
                </td>
              </tr>
            ) : (
              tickets.map((t, i) => (
                <tr
                  key={t._id}
                  onClick={() => setSelectedTicket(t)}
                  style={{
                    borderBottom: '1px solid rgba(255,255,255,0.04)',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(0,229,255,0.03)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <td style={{ color: cyan, fontSize: 12, padding: '11px 14px', fontFamily: 'monospace', fontWeight: 600 }}>
                    {t.ticketId}
                  </td>
                  <td style={{ color: '#fff', fontSize: 12, padding: '11px 14px' }}>
                    <div style={{ fontSize: 12, fontWeight: 500 }}>{t.userId?.name || <span style={{ color: '#555' }}>Deleted user</span>}</div>
                    <div style={{ fontSize: 10, color: '#888' }}>{t.userId?.userCode || '—'}</div>
                  </td>
                  <td style={{ color: '#888', fontSize: 12, padding: '11px 14px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.title}
                  </td>
                  <td style={{ padding: '11px 14px' }}>
                    <span style={{ background: `${statusColors[t.status]}20`, color: statusColors[t.status], border: `1px solid ${statusColors[t.status]}40`, borderRadius: 6, padding: '2px 8px', fontSize: 10, fontWeight: 600, textTransform: 'capitalize' }}>
                      {t.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td style={{ color: '#555', fontSize: 11, padding: '11px 14px', whiteSpace: 'nowrap' }}>
                    {fmtIST(t.createdAt)}
                  </td>
                  <td style={{ padding: '11px 14px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={(e) => { e.stopPropagation(); setSelectedTicket(t); }}
                      style={{
                        background: 'rgba(0,229,255,0.1)',
                        border: '1px solid rgba(0,229,255,0.3)',
                        color: cyan,
                        borderRadius: 6,
                        padding: '4px 10px',
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
          {Array.from({ length: Math.min(pages, 5) }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              onClick={() => setPage(p)}
              style={{
                background: p === page ? cyan : 'rgba(255,255,255,0.06)',
                border: 'none',
                color: p === page ? '#000' : '#888',
                borderRadius: 6,
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {p}
            </button>
          ))}
        </div>
      )}

      {/* Modal */}
      {selectedTicket && <AdminViewSupportTicket ticket={selectedTicket} onClose={() => { setSelectedTicket(null); load(); }} />}
    </div>
  );
};

export default AdminSupportTickets;
